import { FC, ReactNode } from "react";
import {
  Box,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Block, Scoreboard, Style } from "@mui/icons-material";
import { SanctionEventTypeKeys } from "@/models/ticker";
import { formatScore } from "@context/utils";
import { TeamReference } from "@/models/pdfData";
import YellowCard from "../custom-icons/YellowCard";
import RedCard from "../custom-icons/RedCard";
import ExpulsionCard from "../custom-icons/ExpulsionCard";
import DisqualificationCard from "../custom-icons/DisqualificationCard";
import { Official, Player } from "@/models/gameGeneral";
import { gameState, useTeamSides } from "@/services/gameState";
import { useAppDataContext } from "@context/AppContext";
import DelayYellowCard from "../custom-icons/DelayYellowCard";
import DelayRedCard from "../custom-icons/DelayRedCard";
import {
  CenteredHeaderTableCell,
  CenteredTableCell,
} from "../table/TableComponents";
import { getAddressee } from "@/utils/persons";

interface TeamSanctionsProps {
  side: "left" | "right";
  team: TeamReference;
}

const SanctionSymbolMap: { [key in SanctionEventTypeKeys]: () => ReactNode } = {
  TEAM_IMPROPER_REQUEST: () => <Block sx={{ fontSize: "2.5vw" }} />,
  TEAM_WARNING: () => <DelayYellowCard sx={{ fontSize: "2.5vw" }} />,
  TEAM_PENALTY: () => <DelayRedCard sx={{ fontSize: "2.5vw" }} />,
  INDIVIDUAL_WARNING: () => <YellowCard sx={{ fontSize: "2.5vw" }} />,
  INDIVIDUAL_PENALTY: () => <RedCard sx={{ fontSize: "2.5vw" }} />,
  INDIVIDUAL_EXPULSION: () => <ExpulsionCard sx={{ fontSize: "2.5vw" }} />,
  INDIVIDUAL_DISQUALIFICATION: () => (
    <DisqualificationCard sx={{ fontSize: "2.5vw" }} />
  ),
};

const getSanctionOrAddressee = (
  type: SanctionEventTypeKeys,
  person?: Official | Player,
): string => {
  if (!person) {
    switch (type) {
      case "TEAM_WARNING":
        return "Verzögerung (V)";
      case "TEAM_PENALTY":
        return "Verzögerung (B)";
      case "TEAM_IMPROPER_REQUEST":
        return "unber. Antrag";
      default:
        return type;
    }
  } else {
    return getAddressee(person);
  }
};

const TeamSanctions: FC<TeamSanctionsProps> = ({ team }) => {
  const sanctions = gameState("sanctions");
  const teamSanctions = sanctions[team];
  return (
    <TableContainer>
      <Table size="small" sx={{ tableLayout: "fixed" }}>
        <TableHead>
          <TableRow>
            <CenteredHeaderTableCell
              style={{
                lineHeight: 0,
                width: "11vw",
              }}
            >
              <Scoreboard
                sx={{
                  verticalAlign: "middle",
                  fontSize: "clamp(15px, 4vw, 24px)",
                }}
              />
            </CenteredHeaderTableCell>
            <CenteredHeaderTableCell
              style={{
                lineHeight: 0,
              }}
            >
              <Style
                sx={{
                  transform: "scaleY(-1)",
                  verticalAlign: "middle",
                  fontSize: "clamp(15px, 4vw, 24px)",
                }}
              />
            </CenteredHeaderTableCell>
          </TableRow>
        </TableHead>
        <TableBody sx={{ borderCollapse: "collapse" }}>
          {teamSanctions.map((s, i) => {
            return (
              <TableRow key={`sanctions-${team}-${i}`}>
                <CenteredTableCell sx={{ width: 0 }}>
                  <Typography
                    fontSize={"2.5vw"}
                    whiteSpace={"nowrap"}
                  >{`${s.set} | ${formatScore(s.score, team)}`}</Typography>
                </CenteredTableCell>
                <CenteredTableCell padding={"none"}>
                  <Box
                    display={"flex"}
                    alignItems={"center"}
                    justifyContent={"flex-start"}
                    fontSize={"2.5vw"}
                    gap={"2vw"}
                  >
                    {SanctionSymbolMap[s.type]()}
                    <Typography
                      fontSize={"2.5vw"}
                      overflow={"hidden"}
                      whiteSpace={"nowrap"}
                      textOverflow={"ellipsis"}
                    >
                      {getSanctionOrAddressee(s.type, s.person)}
                    </Typography>
                  </Box>
                </CenteredTableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const LeftTeamSanctions: FC<Omit<TeamSanctionsProps, "side">> = (props) =>
  TeamSanctions({ side: "left", ...props });
const RightTeamSanctions: FC<Omit<TeamSanctionsProps, "side">> = (props) =>
  TeamSanctions({ side: "right", ...props });

const RefereeTabletSanctions: FC = () => {
  const { leftTeam, rightTeam } = useTeamSides(useAppDataContext());

  return (
    <Box>
      <Typography textAlign={"center"} variant={"h6"}>
        Sanktionen:
      </Typography>
      <Box
        display={"grid"}
        gap={1}
        gridTemplateColumns={"repeat(2, minmax(0, 1fr))"}
      >
        <LeftTeamSanctions team={leftTeam} />
        <RightTeamSanctions team={rightTeam} />
      </Box>
    </Box>
  );
};

export default RefereeTabletSanctions;
